/**
 * Validation Utilities
 * Runtime validation and error checking for timeline data
 */

import type { TruckTimelineData, TimelineViewport } from '../types';

/**
 * Validates viewport data
 */
export const validateViewport = (viewport: TimelineViewport): boolean => {
  if (!viewport) return false;
  if (!(viewport.startDate instanceof Date) || isNaN(viewport.startDate.getTime())) {
    return false;
  }
  if (!(viewport.endDate instanceof Date) || isNaN(viewport.endDate.getTime())) {
    return false;
  }
  if (viewport.startDate >= viewport.endDate) {
    return false;
  }
  if (typeof viewport.monthsRange !== 'number' || viewport.monthsRange <= 0) {
    return false;
  }
  return true;
};

/**
 * Validates truck timeline data
 */
export const validateTruckData = (data: TruckTimelineData[]): boolean => {
  if (!Array.isArray(data)) return false;

  return data.every((truck) => {
    if (!truck.truckId || !truck.truckName) return false;
    if (!Array.isArray(truck.periods)) return false;

    return truck.periods.every((period) => {
      if (!period.id || !period.type || !period.color) return false;
      if (!(period.startDate instanceof Date) || isNaN(period.startDate.getTime())) {
        return false;
      }
      if (!(period.endDate instanceof Date) || isNaN(period.endDate.getTime())) {
        return false;
      }
      if (period.startDate >= period.endDate) return false;
      return true;
    });
  });
};

/**
 * Safely gets truck name with fallback
 */
export const safeTruckName = (truck: TruckTimelineData): string => {
  return truck?.truckName || truck?.vin || truck?.truckId || 'Unknown Truck';
};

/**
 * Checks if a number is valid and finite
 */
export const isValidNumber = (num: unknown): num is number => {
  return typeof num === 'number' && !isNaN(num) && isFinite(num);
};

/**
 * Safely calculates dimensions with fallback
 */
export const safeDimension = (value: number, fallback: number, min = 0): number => {
  if (!isValidNumber(value)) return fallback;
  return Math.max(min, value);
};
